import { useState } from 'react';
import { useDialogFocus } from '../shared/hooks/useDialogFocus';

type AuthMode = 'login' | 'signup';

export interface SignupData {
  name: string;
  phone: string;
  email: string;
}

export interface LoginData {
  email: string;
}

interface SignupModalProps {
  onClose: () => void;
  onSignup: (data: SignupData) => Promise<void> | void;
  onLogin: (data: LoginData) => Promise<void> | void;
}

function SignupModal({ onClose, onSignup, onLogin }: SignupModalProps) {
  const [mode, setMode] = useState<AuthMode>('login');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const dialogRef = useDialogFocus(true, onClose);

  const isLogin = mode === 'login';
  const canSubmit = isLogin
    ? email.trim() !== ''
    : name.trim() !== '' && phone.trim() !== '' && email.trim() !== '';

  const switchMode = (nextMode: AuthMode) => {
    if (isSubmitting || nextMode === mode) return;
    setMode(nextMode);
    setErrorMessage('');
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    setErrorMessage('');
    try {
      if (isLogin) {
        await onLogin({ email: email.trim() });
      } else {
        await onSignup({ name: name.trim(), phone: phone.trim(), email: email.trim() });
      }
      onClose();
    } catch (err: unknown) {
      const fallback = isLogin ? '로그인에 실패했습니다.' : '회원가입에 실패했습니다.';
      setErrorMessage(err instanceof Error ? err.message : fallback);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="signup-modal-backdrop">
      <section
        ref={dialogRef as React.RefObject<HTMLElement>}
        className="signup-modal"
        role="dialog"
        tabIndex={-1}
        aria-modal="true"
        aria-labelledby="signup-modal-title"
      >
        <header className="signup-modal__header">
          <h2 id="signup-modal-title">{isLogin ? '로그인' : '회원가입'}</h2>
          <button className="signup-modal__close" type="button" onClick={onClose} aria-label="닫기">
            <span aria-hidden="true">×</span>
          </button>
        </header>

        <div className="signup-modal__tabs" aria-label="로그인 또는 회원가입">
          <button
            className={isLogin ? 'is-active' : undefined}
            type="button"
            aria-pressed={isLogin}
            disabled={isSubmitting}
            onClick={() => switchMode('login')}
          >
            로그인
          </button>
          <button
            className={!isLogin ? 'is-active' : undefined}
            type="button"
            aria-pressed={!isLogin}
            disabled={isSubmitting}
            onClick={() => switchMode('signup')}
          >
            회원가입
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          {!isLogin && (
            <>
              <label className="signup-modal__field">
                이름
                <input type="text" value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" required />
              </label>
              <label className="signup-modal__field">
                전화번호
                <input type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} autoComplete="tel" required />
              </label>
            </>
          )}
          <label className="signup-modal__field">
            이메일
            <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} autoComplete="email" required />
          </label>

          {errorMessage ? <p className="signup-modal__error" role="alert">{errorMessage}</p> : null}

          <div className="signup-modal__actions">
            <button className="signup-modal__primary" type="submit" disabled={!canSubmit || isSubmitting}>
              {isSubmitting
                ? (isLogin ? '로그인 중...' : '가입 중...')
                : (isLogin ? '로그인' : '가입하기')}
            </button>
            <button className="signup-modal__secondary" type="button" onClick={onClose}>취소</button>
          </div>
        </form>
      </section>
    </div>
  );
}

export default SignupModal;
